import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { onTicketNotification, TicketNotifPayload } from '../../utils/ticketNotificationManager';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TicketToastData extends TicketNotifPayload {
    id: string;
    timestamp: number;
}

export type { TicketNotifPayload };

// ─── Colours / Icons ──────────────────────────────────────────────────────────

const PRIORITY_COLOR: Record<string, string> = {
    CRITICAL: '#dc2626',
    HIGH: '#ea580c',
    MEDIUM: '#d97706',
    LOW: '#059669',
};

const TYPE_GRADIENT: Record<string, string> = {
    HEALTH: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    TRANSFER: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    VACCINATION: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
    PAYMENT: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
};

const TYPE_ICON: Record<string, string> = {
    HEALTH: '🏥',
    TRANSFER: '🔄',
    VACCINATION: '💉',
    PAYMENT: '💳',
};

// ─── Single Toast Card ────────────────────────────────────────────────────────

const SingleToast: React.FC<{ toast: TicketToastData; onClose: () => void }> = ({ toast, onClose }) => {
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Animate in
        const t = setTimeout(() => setVisible(true), 20);
        // Auto-dismiss after 8s
        const d = setTimeout(() => {
            setVisible(false);
            setTimeout(onClose, 400);
        }, 8000);
        return () => { clearTimeout(t); clearTimeout(d); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const type = (toast.ticketType || 'HEALTH').toUpperCase();
    const isPayment = type === 'PAYMENT';
    const gradient = TYPE_GRADIENT[type] || TYPE_GRADIENT.HEALTH;
    const icon = TYPE_ICON[type] || '🎫';
    const prColor = PRIORITY_COLOR[toast.priority?.toUpperCase() || 'MEDIUM'] || '#d97706';
    const badgeColor = isPayment ? '#0891b2' : '#059669';

    return (
        <div
            style={{
                width: 360,
                borderRadius: 16,
                background: '#fff',
                boxShadow: '0 20px 60px rgba(0,0,0,0.22), 0 4px 20px rgba(0,0,0,0.12)',
                overflow: 'hidden',
                transform: visible ? 'translateX(0) scale(1)' : 'translateX(120%) scale(0.9)',
                opacity: visible ? 1 : 0,
                transition: 'transform 0.4s cubic-bezier(0.175,0.885,0.32,1.275), opacity 0.4s ease',
                marginBottom: 12,
                position: 'relative',
            }}
        >
            {/* Top accent bar */}
            <div style={{ background: gradient, height: 5, width: '100%' }} />

            {/* Countdown bar */}
            <div style={{
                position: 'absolute', bottom: 0, left: 0, height: 3,
                background: gradient, borderRadius: '0 0 0 16px',
                animation: 'fv-toast-countdown 8s linear forwards',
            }} />

            <div style={{ padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {/* Icon */}
                <div style={{
                    width: 46, height: 46, borderRadius: 12, background: gradient,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, flexShrink: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}>
                    {icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        <CheckCircle2 size={13} color={badgeColor} strokeWidth={2.5} />
                        <span style={{ fontSize: 10, fontWeight: 800, color: badgeColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {isPayment ? 'New Payment!' : 'Ticket Raised!'}
                        </span>
                        {toast.priority && (
                            <span style={{
                                fontSize: 9, fontWeight: 800, color: prColor,
                                background: `${prColor}1a`, borderRadius: 4,
                                padding: '1px 5px', letterSpacing: '0.04em',
                            }}>
                                {toast.priority}
                            </span>
                        )}
                    </div>

                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>
                        {toast.title}
                    </p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
                        {toast.body}
                    </p>

                    <button
                        onClick={() => {
                            navigate(isPayment ? '/farmvest/payments' : (toast.ticketId ? `/farmvest/tickets?highlight=${toast.ticketId}` : '/farmvest/tickets'));
                            setVisible(false);
                            setTimeout(onClose, 400);
                        }}
                        style={{
                            marginTop: 10, display: 'flex', alignItems: 'center', gap: 5,
                            fontSize: 12, fontWeight: 700, color: badgeColor,
                            background: isPayment ? '#ecfeff' : '#f0fdf4',
                            border: `1px solid ${isPayment ? '#a5f3fc' : '#bbf7d0'}`,
                            borderRadius: 7, padding: '5px 10px', cursor: 'pointer',
                        }}
                    >
                        {isPayment ? 'View Payments' : 'View Ticket'} <ArrowRight size={11} />
                    </button>
                </div>

                {/* Close */}
                <button
                    onClick={() => { setVisible(false); setTimeout(onClose, 400); }}
                    style={{
                        width: 26, height: 26, borderRadius: 7, border: 'none',
                        background: '#f3f4f6', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <X size={13} color="#6b7280" />
                </button>
            </div>

            <style>{`
        @keyframes fv-toast-countdown {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
        </div>
    );
};

// ─── Container — mounts itself via event bus (no Redux needed) ────────────────

const TicketNotificationToast: React.FC = () => {
    const [toasts, setToasts] = useState<TicketToastData[]>([]);

    useEffect(() => {
        // Listen for notifications from anywhere in the app
        const unsubscribe = onTicketNotification((payload) => {
            const newToast: TicketToastData = {
                ...payload,
                id: `tn-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                timestamp: Date.now(),
            };
            setToasts(prev => {
                // Keep max 4
                const next = [...prev.slice(-3), newToast];
                return next;
            });
        });
        return unsubscribe;
    }, []);

    const remove = (id: string) => setToasts(prev => prev.filter(t => t.id !== id));

    if (toasts.length === 0) return null;

    return createPortal(
        <div style={{
            position: 'fixed',
            top: 80,
            right: 24,
            zIndex: 2147483647, // max z-index
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            pointerEvents: 'none',
        }}>
            {toasts.map(t => (
                <div key={t.id} style={{ pointerEvents: 'auto' }}>
                    <SingleToast toast={t} onClose={() => remove(t.id)} />
                </div>
            ))}
        </div>,
        document.body
    );
};

export default TicketNotificationToast;
